import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Loader2 } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Card } from "../../../../components/ui/Card";
import { Skeleton } from "../../../../components/ui/Skeleton";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import { showToast } from "../../../../utils/showToast";
import {
  createAiAgent,
  getAiAgent,
  isEndpointMissing,
  testAiAgent,
  updateAiAgent,
} from "./aiAgentsApi";
import {
  agentToState,
  buildAgentPayload,
  capabilitiesForAudience,
  customEndpointTarget,
  defaultAgentState,
  markSaved,
  validateAgentForm,
} from "./agentPayload";
import IdentitySection from "./agentSections/IdentitySection";
import ScopeSection from "./agentSections/ScopeSection";
import BehaviorSection from "./agentSections/BehaviorSection";
import StyleSection from "./agentSections/StyleSection";
import PromptSection from "./agentSections/PromptSection";
import ProviderSection from "./agentSections/ProviderSection";
import CapabilitiesSection from "./agentSections/CapabilitiesSection";
import LimitsSection from "./agentSections/LimitsSection";
import { PRIMARY_BTN_CLS, SECONDARY_BTN_CLS } from "./agentSections/primitives";
import { aiErrorMessage, capabilityLabeler, normalizeCaller } from "./aiErrorMessage";
import { CUSTOM_PROVIDER_ID } from "./aiModelCatalog";

// Editor de un agente IA del helpdesk (crear y editar).
//
// `agentId === "new"` arma un formulario vacío con los defaults del contrato;
// con un id real carga el agente (Skeleton mientras tanto). El guardado es un
// PUT PARCIAL: solo viajan las secciones que el usuario tocó — el trabajo de
// comparar contra el GET vive en agentPayload.js, no acá.
//
// SEGURIDAD: el token del proveedor se guarda SOLO en el estado efímero de
// este componente mientras el usuario lo tipea (input type=password); nunca se
// renderiza el guardado (del backend solo llegan has_token/token_last4), nunca
// se loguea y viaja únicamente en el body del POST/PUT cuando se tipeó uno
// nuevo.
//
// Las secciones viven en agentSections/ y reciben su porción del estado: así el
// editor no se convierte en un archivo de mil líneas y cada sección se puede
// leer sola.

// Documento del agente a partir de una respuesta del BFF. Un 200 sin `agent`
// (o con cualquier otro body) NO es un agente: devolver ese body haría que el
// formulario se pintara con los defaults y que el baseline quedara apuntando a
// un agente inventado — el próximo PUT mandaría el documento completo.
function extractAgent(res) {
  const candidate = res?.agent && typeof res.agent === "object" ? res.agent : res;
  return candidate && typeof candidate.id === "string" && candidate.id ? candidate : null;
}

// Al cambiar de audiencia se descartan las capacidades que el catálogo marca
// como de OTRA audiencia (un agente de reparto no consulta pedidos de
// clientes). Las que el catálogo NO conoce se conservan: un catálogo a medias
// —o aún no desplegado— no es motivo para borrar la selección del admin.
function keepCapabilitiesForAudience(capabilities, catalog, audience) {
  const known = new Set(
    (Array.isArray(catalog?.capabilities) ? catalog.capabilities : [])
      .map((c) => c?.id)
      .filter(Boolean),
  );
  if (known.size === 0) return capabilities;
  const allowed = capabilitiesForAudience(catalog, audience);
  const allowedIds = new Set([...allowed.read, ...allowed.write].map((c) => c.id));
  const keep = (id) => allowedIds.has(id) || !known.has(id);
  return {
    read: capabilities.read.filter(keep),
    write: capabilities.write.filter(keep),
  };
}

// Envoltorio del subárbol que el rol del llamador no puede tocar. Un fieldset
// deshabilitado apaga TODOS los controles de adentro (input, select, textarea,
// button) sin cablear `disabled` sección por sección, y no se puede re-eximir
// desde adentro: por eso el estado del agente —la única excepción— y el botón de
// guardar viven FUERA de él. El fieldset conserva su caja (nada de
// display:contents), así los divisores del editor y el espaciado no cambian.
function LockableSection({ locked, children }) {
  return (
    <fieldset disabled={locked || undefined} className="min-w-0 border-0 p-0 m-0">
      {children}
    </fieldset>
  );
}

// Espeja las secciones reales para que nada salte al cargar.
function AgentEditorSkeleton() {
  return (
    <div className="divide-y divide-border" aria-hidden="true">
      <div className="pb-6 space-y-3">
        <Skeleton className="h-4 w-28 rounded" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-11 md:h-8 rounded-md" />
          <Skeleton className="h-11 md:h-8 rounded-md" />
        </div>
        <Skeleton className="h-16 rounded-md" />
        <Skeleton className="h-11 md:h-8 w-64 rounded-md" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-36 rounded" />
        <Skeleton className="h-5 rounded" />
        <Skeleton className="h-5 rounded" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-44 rounded" />
        <Skeleton className="h-11 md:h-8 rounded-md" />
        <Skeleton className="h-11 md:h-8 rounded-md" />
        <Skeleton className="h-11 md:h-8 rounded-md" />
      </div>
      <div className="py-6 space-y-3">
        <Skeleton className="h-4 w-40 rounded" />
        <Skeleton className="h-40 rounded-md" />
      </div>
      <div className="pt-6">
        <Skeleton className="h-11 md:h-8 w-40 rounded-md" />
      </div>
    </div>
  );
}

export default function AgentEditor({
  agentId,
  catalog,
  scope,
  tenantSettings,
  // El listado de agentes SÍ respondió (el padre lo sabe): un 404 en el GET de
  // este agente es, entonces, "este agente no existe" y no "la ruta no está
  // desplegada". Sin este dato, los dos casos son indistinguibles desde acá.
  agentsAvailable = false,
  // Audiencia preseleccionada para un agente nuevo (llega de la URL del padre).
  initialAudience = "customer",
  // Permisos del llamador. Viajan adentro del catálogo (el padre ya lo carga),
  // así que este prop es solo para poder montarlos sueltos en un test; sin él,
  // normalizeCaller() deja todo permitido —el contrato es aditivo.
  caller,
  onSaved,
  onCancel,
}) {
  const { t } = useTranslation();
  const isNew = agentId === "new";
  const permissions = normalizeCaller(caller ?? catalog?.caller);

  // Un rol que no administra el proveedor no lo manda al crear, y el backend
  // hace nacer ese agente heredando el del courier: el formulario arranca así
  // para mostrar lo que de verdad se va a guardar.
  const canManageProvider = permissions.can_manage_provider;
  const [state, setState] = useState(() =>
    isNew
      ? defaultAgentState(catalog, tenantSettings, scope, initialAudience, { canManageProvider })
      : null,
  );
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);
  // El agente está configurado por encima del alcance del rol: se puede leer y
  // pausar, nada más. Vive fuera del estado del formulario porque no es un
  // campo editable — lo dice el documento, no el usuario.
  const [lockedForCaller, setLockedForCaller] = useState(false);
  // El agente que se pidió ya no está (404 con la ruta viva): estado propio
  // porque la salida es otra — volver a la lista, no reintentar.
  const [agentGone, setAgentGone] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // El token que el usuario está tipeando AHORA. Vive solo acá y se limpia al
  // guardar; jamás entra al estado del formulario.
  const [typedToken, setTypedToken] = useState("");
  // Se borró un token tipeado por cambiar de proveedor: el campo se vacía solo y
  // hay que decirlo (si no, parece que se perdió lo escrito).
  const [tokenCleared, setTokenCleared] = useState(false);
  const [testState, setTestState] = useState("idle"); // idle|testing|ok|error
  const [testError, setTestError] = useState("");
  const [saveState, setSaveState] = useState("idle"); // idle|saving
  const [saveError, setSaveError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const dirtyRef = useRef(false);
  const nameRef = useRef(null);
  const assistantNameRef = useRef(null);
  const baseUrlRef = useRef(null);
  const modelRef = useRef(null);
  const tokenRef = useRef(null);

  // Refs espejo para efectos y callbacks con deps estables.
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const catalogRef = useRef(catalog);
  catalogRef.current = catalog;
  // `t` cambia de identidad cuando cambia el idioma de la interfaz: si entrara
  // a las deps del GET, traducir la pantalla recargaría el agente y borraría lo
  // editado sin guardar.
  const tRef = useRef(t);
  tRef.current = t;

  const handleRetry = useCallback(() => setReloadKey((k) => k + 1), []);

  // Agente existente: el GET rellena el formulario (y fija el baseline del
  // PUT parcial). El alcance entra por ref: no queremos recargar en cada
  // render del padre.
  useEffect(() => {
    if (isNew) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setAgentGone(false);
    (async () => {
      try {
        const res = await getAiAgent(agentId);
        if (!alive) return;
        const agent = extractAgent(res);
        if (!agent) {
          setLoadError(tRef.current("No se pudo cargar el agente."));
          return;
        }
        setState(agentToState(agent, scopeRef.current));
        setLockedForCaller(agent.locked_for_caller === true);
        setBackendMissing(false);
      } catch (err) {
        if (!alive) return;
        // La ruta responde (el listado cargó) y el agente puntual no está: no es
        // un backend sin desplegar, es un agente borrado o de otro courier.
        if (isEndpointMissing(err) && agentsAvailable) setAgentGone(true);
        else if (isEndpointMissing(err)) setBackendMissing(true);
        else setLoadError(err?.message || tRef.current("No se pudo cargar el agente."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [agentId, agentsAvailable, isNew, reloadKey]);

  // Agente nuevo: los defaults dependen del catálogo y del alcance del
  // courier. Si esas props llegan después del primer render (el padre las
  // carga async) y el usuario todavía no tocó nada, se recomputan.
  useEffect(() => {
    if (!isNew || dirtyRef.current) return;
    setState(
      defaultAgentState(catalog, tenantSettings, scope, initialAudience, { canManageProvider }),
    );
  }, [isNew, catalog, tenantSettings, scope, initialAudience, canManageProvider]);

  // Toda edición marca el formulario como tocado: a partir de ahí nada
  // recomputa defaults por encima de lo que el usuario escribió.
  const updateState = useCallback((updater) => {
    dirtyRef.current = true;
    setState((prev) => (prev ? updater(prev) : prev));
  }, []);

  const handleIdentityChange = useCallback(
    (field, value) => {
      // El error del campo se va al primer cambio: dejarlo pintado (y con
      // aria-invalid) después de corregirlo confunde y hace repetir el aviso.
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      updateState((prev) => {
        if (field !== "audience") return { ...prev, [field]: value };
        // Al cambiar de audiencia se descartan las capacidades que ya no
        // aplican: mandar el id de otra audiencia sería un permiso de más.
        return {
          ...prev,
          audience: value,
          capabilities: keepCapabilitiesForAudience(
            prev.capabilities,
            catalogRef.current,
            value,
          ),
        };
      });
    },
    [updateState],
  );

  const patchSection = useCallback(
    (section, patch) => {
      updateState((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));
    },
    [updateState],
  );

  const handleStyleChange = useCallback(
    (patch) => {
      // Mismo criterio que la identidad: el error de la firma se va al primer
      // cambio del campo.
      if ("assistant_name" in patch) {
        setFieldErrors((prev) => ({ ...prev, assistant_name: undefined }));
      }
      patchSection("style", patch);
    },
    [patchSection],
  );

  const handleScopeChange = useCallback(
    (nextScope) => updateState((prev) => ({ ...prev, scope: nextScope })),
    [updateState],
  );

  const handleProviderChange = useCallback(
    (patch) => {
      setFieldErrors((prev) => ({
        ...prev,
        base_url: undefined,
        model: undefined,
        token: undefined,
      }));
      // La prueba anterior era de OTRA configuración (proveedor, modelo o URL):
      // dejar el "Conexión OK" en pantalla sería mentir sobre una prueba que no
      // cubre lo que se está guardando.
      setTestState("idle");
      setTestError("");
      // Cambiar de proveedor descarta el token TIPEADO: viajaba como credencial
      // del proveedor anterior y guardarlo contra el nuevo dejaría al agente
      // autenticándose con la llave equivocada. Se avisa por el hint del campo;
      // volver a tipearlo (o al proveedor original) lo restituye.
      if (patch.kind !== undefined && typedToken) {
        setTypedToken("");
        setTokenCleared(true);
      } else if (patch.kind !== undefined) {
        setTokenCleared(false);
      }
      dirtyRef.current = true;
      setState((prev) => {
        const next = { ...patch };
        // Cambiar de proveedor invalida el token guardado: es la credencial del
        // proveedor anterior, y arrastrarla al nuevo dejaría al agente hablando
        // con un gateway ajeno usando la llave equivocada. Con un endpoint propio
        // ('custom') pasa lo mismo al cambiar su URL base: el backend descarta el
        // token si el destino cambia y no llega uno nuevo, así que el formulario
        // tiene que pedirlo. Volver al proveedor (y URL) guardados lo restituye
        // (el backend lo conserva mientras no se mande uno nuevo).
        if (patch.kind !== undefined || patch.base_url !== undefined) {
          const saved = prev.baseline?.provider;
          const kind = patch.kind !== undefined ? patch.kind : prev.provider.kind;
          const baseUrl = patch.base_url !== undefined ? patch.base_url : prev.provider.base_url;
          const sameTarget =
            saved?.kind === kind &&
            (kind !== CUSTOM_PROVIDER_ID ||
              customEndpointTarget(saved.base_url) === customEndpointTarget(baseUrl));
          next.has_token = sameTarget ? saved.has_token === true : false;
          next.token_last4 = sameTarget ? saved.token_last4 || "" : "";
        }
        return { ...prev, provider: { ...prev.provider, ...next } };
      });
    },
    [typedToken],
  );

  const handleTokenChange = useCallback((value) => {
    setTypedToken(value);
    // Un token nuevo deja atrás el aviso de "se borró al cambiar de proveedor".
    setTokenCleared(false);
    setFieldErrors((prev) => ({ ...prev, token: undefined }));
    // Un token nuevo invalida el resultado de la prueba anterior.
    setTestState("idle");
    setTestError("");
  }, []);

  // ── Permisos del rol ─────────────────────────────────────────────────────
  // Derivados ANTES de los callbacks que los leen: `const` no se puede
  // referenciar antes de su inicialización, y `t`/`capabilityLabelFor` entran en
  // las deps de esos callbacks (se evalúan durante el render).
  //
  // Un agente fuera del alcance del rol se puede leer y pausar, nada más.
  const locked = !isNew && lockedForCaller;
  // La sección de proveedor queda cerrada si el rol no la administra o si el
  // agente entero está fuera de su alcance; en ambos casos sus campos NO viajan
  // en el payload (ver buildAgentPayload).
  const providerReadOnly = locked || !permissions.can_manage_provider;
  // Diff real contra el baseline —el mismo que arma el payload— para que con el
  // agente bloqueado el único guardado posible sea el estado, y solo a "pausado".
  const lockedPayloadKeys = locked
    ? Object.keys(buildAgentPayload(state, { isNew, typedToken, allowProvider: false }))
    : [];
  const lockedPauseOnly =
    locked &&
    state?.status === "paused" &&
    lockedPayloadKeys.length === 1 &&
    lockedPayloadKeys[0] === "status";
  const capabilityLabelFor = capabilityLabeler(catalog);

  const handleTest = useCallback(async () => {
    if (isNew) return;
    setTestState("testing");
    setTestError("");
    try {
      // api_token solo si hay algo tipeado; sin él el backend usa el token
      // guardado del agente.
      const payload = {};
      const typed = typedToken.trim();
      if (typed) payload.api_token = typed;
      const res = await testAiAgent(agentId, payload);
      if (res?.ok) {
        setTestState("ok");
      } else {
        setTestState("error");
        setTestError(res?.error || t("El proveedor rechazó la conexión."));
      }
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setTestState("idle");
      } else {
        setTestState("error");
        // Un 403 tipado de la prueba (rol sin permiso sobre la credencial) se
        // explica con su código; el resto cae al mensaje del servidor.
        setTestError(aiErrorMessage(err, t, capabilityLabelFor) || t("No se pudo probar la conexión."));
      }
    }
  }, [agentId, isNew, typedToken, capabilityLabelFor, t]);

  const handleSave = useCallback(async () => {
    // Con el agente bloqueado no hay nada que validar: lo único que puede
    // viajar es la pausa, y las reglas del formulario (nombre, proveedor) no
    // entran en ese payload. Correrlas trabaría la pausa por un campo que este
    // rol no puede corregir —y el foco iría a un input deshabilitado.
    const errors = locked
      ? {}
      : validateAgentForm(state, typedToken, { allowProvider: !providerReadOnly });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Foco al primer control ofensor, en el orden del formulario.
      if (errors.name) nameRef.current?.focus();
      else if (errors.assistant_name) assistantNameRef.current?.focus();
      else if (errors.base_url) baseUrlRef.current?.focus();
      else if (errors.model) modelRef.current?.focus();
      else if (errors.token) tokenRef.current?.focus();
      return;
    }

    setFieldErrors({});
    setSaveState("saving");
    setSaveError("");
    try {
      const payload = buildAgentPayload(state, {
        isNew,
        typedToken,
        allowProvider: !providerReadOnly,
      });
      const res = isNew ? await createAiAgent(payload) : await updateAiAgent(agentId, payload);
      const agent = extractAgent(res);
      // El formulario se rearma con lo que confirmó el backend (mascara del
      // token incluida) y el input del token se limpia: nunca queda visible ni
      // reenviable por accidente. Sin documento de vuelta, al menos se refresca
      // el baseline para que el próximo PUT siga siendo parcial.
      setState((prev) => (agent ? agentToState(agent, scopeRef.current) : markSaved(prev)));
      if (agent) setLockedForCaller(agent.locked_for_caller === true);
      setTypedToken("");
      setTokenCleared(false);
      setTestState("idle");
      setTestError("");
      setSaveState("idle");
      showToast(t("Agente guardado."), "success");
      onSaved?.(agent);
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setSaveState("idle");
      } else {
        setSaveState("idle");
        // Los 403 del contrato nuevo (rol sin permiso, capacidad no concedible,
        // agente fuera de alcance) se explican solos; el resto cae al mensaje
        // del servidor o, en su defecto, al genérico de la pantalla.
        setSaveError(
          aiErrorMessage(err, t, capabilityLabelFor) || t("No se pudo guardar el agente."),
        );
      }
    }
  }, [
    agentId,
    isNew,
    state,
    typedToken,
    providerReadOnly,
    locked,
    capabilityLabelFor,
    t,
    onSaved,
  ]);

  if (loading) return <AgentEditorSkeleton />;

  if (backendMissing) {
    return (
      <div className="space-y-2">
        <PendingBackendNotice>
          {t("Esta función está en construcción.")}
        </PendingBackendNotice>
        {/* Con la ruta caída igual se puede reintentar: una vez en este estado
            no habría otra salida si el backend vuelve a responder. */}
        <button
          type="button"
          onClick={handleRetry}
          className="h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
        >
          {t("Reintentar")}
        </button>
      </div>
    );
  }

  if (agentGone) {
    return (
      <Card padding="lg" className="max-w-md mx-auto text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs text-fg">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-status-warning" aria-hidden="true" />
          {t("Este agente ya no existe.")}
        </div>
        {/* Reintentar no tiene sentido: el agente no está. La salida es volver. */}
        {onCancel && (
          <button type="button" onClick={onCancel} className={cn(SECONDARY_BTN_CLS, "mt-3")}>
            {t("Volver a la lista")}
          </button>
        )}
      </Card>
    );
  }

  if (loadError || !state) {
    return (
      <Card padding="lg" className="max-w-md mx-auto text-center">
        <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {loadError || t("No se pudo cargar el agente.")}
        </div>
        <button
          type="button"
          onClick={handleRetry}
          className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
        >
          {t("Reintentar")}
        </button>
      </Card>
    );
  }

  return (
    <div className="divide-y divide-border">
      {/* El agente está por encima del alcance del rol: se dice ARRIBA de todo,
          antes de que el usuario intente tocar un campo que no se puede tocar. */}
      {locked && (
        <div className="pb-6">
          <div
            id="agent-locked-help"
            role="note"
            className="flex items-start gap-2.5 rounded-lg border border-status-warning/30 bg-status-warning-soft/60 px-3 py-2.5"
          >
            <AlertTriangle
              className="w-4 h-4 shrink-0 mt-0.5 text-status-warning"
              aria-hidden="true"
            />
            <p className="text-[12px] md:text-[11px] leading-relaxed text-fg">
              {t(
                "Este agente fue configurado con permisos mayores a los de tu rol. Solo puedes pausarlo.",
              )}
            </p>
          </div>
        </div>
      )}

      <div className="pb-6">
        {/* Identidad queda fuera del bloqueo: su control de ESTADO es la única
            acción permitida (pausar), y un fieldset deshabilitado no se puede
            re-eximir desde adentro. */}
        <IdentitySection
          state={state}
          audiences={catalog?.audiences}
          errors={fieldErrors}
          nameRef={nameRef}
          onChange={handleIdentityChange}
          locked={locked}
        />
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <ScopeSection value={state.scope} audience={state.audience} onChange={handleScopeChange} />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <BehaviorSection
            value={state.behavior}
            onChange={(patch) => patchSection("behavior", patch)}
            // El modo automático es admin_only en el backend: mismo tier que
            // expone can_manage_service_account.
            canUseAuto={permissions.can_manage_service_account}
          />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <StyleSection
            value={state.style}
            onChange={handleStyleChange}
            errors={fieldErrors}
            assistantNameRef={assistantNameRef}
          />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <PromptSection value={state.prompt} onChange={(patch) => patchSection("prompt", patch)} />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <ProviderSection
            value={state.provider}
            catalog={catalog}
            isNew={isNew}
            typedToken={typedToken}
            tokenCleared={tokenCleared}
            readOnly={providerReadOnly}
            onChange={handleProviderChange}
            onTokenChange={handleTokenChange}
            onTest={handleTest}
            testState={testState}
            testError={testError}
            errors={fieldErrors}
            baseUrlRef={baseUrlRef}
            modelRef={modelRef}
            tokenRef={tokenRef}
          />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <CapabilitiesSection
            value={state.capabilities}
            audience={state.audience}
            catalog={catalog}
            onChange={(patch) => patchSection("capabilities", patch)}
          />
        </LockableSection>
      </div>

      <div className="py-6">
        <LockableSection locked={locked}>
          <LimitsSection value={state.limits} onChange={(patch) => patchSection("limits", patch)} />
        </LockableSection>
      </div>

      {/* Guardar / Cancelar */}
      <div className="pt-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleSave}
            disabled={saveState === "saving" || (locked && !lockedPauseOnly)}
            aria-describedby={locked ? "agent-locked-help" : undefined}
            className={PRIMARY_BTN_CLS}
          >
            {saveState === "saving" && (
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            )}
            {saveState === "saving"
              ? t("Guardando…")
              : isNew
                ? t("Crear agente")
                : t("Guardar cambios")}
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={saveState === "saving"}
              className={cn(SECONDARY_BTN_CLS)}
            >
              {t("Cancelar")}
            </button>
          )}
          {/* Con el agente bloqueado el guardado solo lleva la pausa: se dice
              qué hacer, porque un botón apagado sin explicación al lado obliga a
              adivinar (el banner de arriba queda lejos del control). Si ya está
              pausado no hay nada que pedir ni que guardar: el aviso sería una
              instrucción imposible de cumplir (el estado ya es "Pausado"). */}
          {locked && !lockedPauseOnly && state?.status !== "paused" && (
            <span className="text-xs text-fg-muted">
              {t("Este agente solo se puede pausar: elige «Pausado» en Estado para guardar.")}
            </span>
          )}
          {saveError && (
            <span className="inline-flex items-center gap-1 text-xs text-status-error" role="alert">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {saveError}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
